/**
 * Tipos da ApiSistema, divisão `pinout-diagrama`.
 *
 * Espelham os DTOs de `ApiSistema/DTOs/PinoutDiagrama/`. Quando um deles mudar
 * lá, muda aqui — não há geração automática, e é de propósito: a lista abaixo é
 * pequena e a revisão manual é o que pega mudança de contrato.
 */

/** Toda listagem do módulo devolve esta forma. A página é de 200. */
export type PaginaResponse<T> = {
  itens: T[]
  total: number
  pagina: number
  porPagina: number
}

// ---------------------------------------------------------------- catálogos

/**
 * `qtd` é quantos pinouts usam o item. Vem de uma consulta agregada única no
 * servidor — é o "usos" do contrato do campo de busca, e é o que ordena a
 * lista pelo que a pessoa mais usa. Não faça uma segunda chamada para isso.
 */
export type ComponenteResponse = {
  id: number
  projetoId: number
  descricao: string
  codigo: string
  apelido: string
  localizacao: string
  tipo: number
  qtd: number
}

export type ConectorResponse = {
  id: number
  projetoId: number
  componenteId: number
  /**
   * O componente dono, resolvido pela API. Não dá para resolver aqui: o
   * catálogo em memória traz uma página de 200 e há projeto com 274
   * componentes, então parte das linhas ficaria sem dono, calada.
   */
  componenteCodigo: string
  componenteDescricao: string
  chicoteId: number
  descricao: string
  qtdPinos: number
  tipoConector: string
  tipoContagem: string
  menorPino: string
  maiorPino: string
  prefixo: string
  apelido: string
  obs: string
  qtd: number
}

export type ConectorPinoResponse = {
  id: number
  projetoId: number
  conectorId: number
  chicoteId: number
  corId: number
  pino: string
  obs: string
  vazio: number
}

/** `descricao` é "1234.3": o que vem antes do ponto é o circuito, o resto é o trecho. */
export type CircuitoResponse = {
  id: number
  projetoId: number
  descricao: string
  apelido: string
  tipo: number
  qtd: number

  /**
   * A cor e o tipo que mais aparecem nos pinouts deste circuito. É o `preenche`
   * do campo de busca: escolheu o circuito, cor e tipo entram sozinhos. Saem do
   * mesmo agrupamento que calcula o `qtd`, então não custam consulta a mais.
   * Circuito sem pinout nenhum vem com zero nos três.
   */
  corHabitualId: number
  corHabitual: string
  tipoHabitual: number
}

export type CorResponse = {
  id: number
  projetoId: number
  descricao: string
  coresId: number
  qtd: number
}

export type ChicoteResponse = {
  id: number
  projetoId: number
  descricao: string
  qtd: number
}

export type RestricaoResponse = {
  id: number
  projetoId: number
  descricao: string
  qtd: number
}

/**
 * O aviso de "gráfico desatualizado" (`projeto.atualizar`) **está escondido por
 * enquanto**, a pedido da produção: a marca acende a cada escrita de pinout e só
 * apaga no job noturno, então ficava acesa em quase todo projeto. O dado
 * continua vindo da API; ligar de novo é trocar para `true`.
 */
export const MOSTRAR_GRAFICO_DESATUALIZADO = false

export type ProjetoResponse = {
  id: number
  data: string
  descricao: string
  concluido: number
  obs: string
  dataAtualizacao: string
  /** RN-05: marcado a cada escrita de pinout. É o aviso de "gráfico desatualizado". */
  atualizar: boolean
  categoria: string
}

/** Pré-flight do get-or-create: 2 = já existe (com id), 1 = não existe. */
export type ConfereResponse = { retorno: 1 | 2; id: number }

// ------------------------------------------------------------------ pinout

export type RestricaoResumo = { id: number; descricao: string }

export type PinoutResponse = {
  id: number
  projetoId: number
  componenteId: number
  /** Separados de propósito: o código é identificador e vai em monoespaçado. */
  componenteCodigo: string
  componenteDescricao: string
  conectorId: number
  conectorDescricao: string
  circuitoId: number
  circuitoDescricao: string
  corId: number
  corDescricao: string
  chicoteId: number
  chicoteDescricao: string
  pino: string
  arquivoOriginal: string
  obs: string
  data: string | null
  tipo: number
  restricoes: RestricaoResumo[]
}

/**
 * Uma linha de inclusão/edição.
 *
 * Cada campo vai por **id ou por descrição**: id quando a pessoa escolheu da
 * lista, descrição quando digitou algo novo — aí a API resolve com
 * get-or-create (RN-03, o `Verifica` do legado). É isso que faz o "+" do
 * formulário custar zero requisição.
 *
 * Atenção ao `id`: ele é o **projeto**, não o pinout. O pinout é `pinoutId`,
 * e `0` cria.
 */
export type PinoutUpsertItem = {
  id: number
  pinoutId: number

  componenteId: number
  componenteCodigo: string
  componenteDescricao: string

  conectorId: number
  conectorDescricao: string

  circuitoId: number
  circuitoDescricao: string
  /** Tipo do circuito escolhido no lançamento; ausente, a API não mexe no tipo. */
  circuitoTipo?: number

  corId: number
  corDescricao: string

  chicoteId: number
  chicoteDescricao: string

  restricaoId: number
  restricaoDescricao: string

  pino: string
  obs: string
  arquivoOriginal: string
  tipo: number
}

export type IncluirPinoutsRequest = { pinouts: PinoutUpsertItem[] }

/**
 * Uma coisa que deu errado numa linha do lote, com o endereço dela.
 * `indice` é a posição no array enviado, contada de zero — é assim que a tela
 * marca a linha. `campo` é o culpado (`pino`, `pinoutId`) ou vazio quando o
 * problema é da linha inteira, como uma duplicidade.
 */
export type MensagemDeLote = {
  indice: number
  campo: string
  texto: string
}

/**
 * O lote é **parcial**, não tudo-ou-nada: a API percorre item a item e o que é
 * duplicado ou incompleto entra em `ignorados` enquanto o resto grava.
 *
 * `idsCriados` traz o que a chamada **inseriu**, na ordem. É o que torna o
 * desfazer possível: a tela guarda a lista e devolve em
 * `POST api/pinouts/excluir-selecionados`. Linha atualizada fica de fora —
 * desfazer atualização exigiria o valor anterior, que ninguém guardou.
 */
export type IncluirPinoutsResponse = {
  criados: number
  atualizados: number
  ignorados: number
  idsCriados: number[]
  mensagens: MensagemDeLote[]
}

// ------------------------------------------------------------------- sessão

/** `POST api/login` — a criar na frente C (fase 0). */
export type LoginRequest = { login: string; senha: string }
export type LoginResponse = { token: string; painelId: number; nome: string }

/** `GET api/acessos/meus` — as chaves do painel logado, já agrupadas. */
export type AcessosResponse = {
  painelId: number
  chaves: string[]
  grupos: { grupo: string; chaves: { chave: string; texto: string }[] }[]
}
